import { lazyOpenBookingDashboard } from 'bookwithme-routes';
import { DatapointStatus, logUsage, PerformanceDatapoint } from 'owa-analytics';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import loc from 'owa-localize';
import { lazyShowNotification } from 'owa-notification-bar';
import { trace } from 'owa-trace';

import deleteMeetingTypeAction from '../actions/deleteMeetingTypeAction';
import setIsPerformingServiceUpdate from '../actions/setIsPerformingServiceUpdate';
import {
    bookWithMeDeleteButtonLabel,
    bookWithMeDeleteConfirmationText,
} from '../components/compose/SidebarButtons.locstring.json';
import {
    bookWithMeDeleteEventFailure,
    bookWithMeDeleteEventSuccess,
} from '../components/shared/NotificationStrings.locstring.json';
import getMeetingTypesFromServer from '../orchestrators/getMeetingTypesFromServer';
import getEntryPoint from '../selectors/getEntryPoint';
import getFirstTimeUser from '../selectors/getFirstTimeUser';
import getMailboxPuid from '../selectors/getMailboxPuid';
import { getUserMailboxGuid } from '../utils/getMailboxGuid';
import isDefaultMeetingType from '../selectors/isDefaultMeetingType';
import deleteBookingMeetingType from '../services/deleteBookingMeetingType';
import deleteMeetingTypeExtension from '../services/deleteMeetingTypeExtension';
import store from '../store/Store';
import deleteDefaultMeetingType from '../utils/deleteDefaultMeetingType';

import { isFeatureEnabled } from 'owa-feature-flags';
import deleteBookingService from 'bookings-shared/lib/c1/services/deleteBookingService';
import { getUserConfiguration } from 'owa-session-store';
import type FeatureSource from 'bookings-shared/lib/c1/constants/source';
import getPrivateAndPublicMeetingTypes from '../selectors/getPrivateAndPublicMeetingTypes';
import { addB2CustomData } from 'bookings-shared/lib/c1/utils/bookingTelemetry';
import { getBookingsRouterStore } from 'bookings-router-store-v2/lib/store';
import changeCurrentRoute from 'bookings-router-store-v2/lib/mutators/changeCurrentRoute';
import { setNavigationStart } from 'bookingsv2-utils/lib/analytics/getTimeFromFetchStart';
import { type BookingsRoute } from 'bookingsv2-constants/lib/bookingsRouteConstants';

/**
 * Triggers a service call to delete a booking meetingType.
 */
export default async function deleteMeetingType(meetingTypeId: string) {
    const isDefaultMT = isDefaultMeetingType(meetingTypeId);
    const meetingTypeStates = getPrivateAndPublicMeetingTypes();
    const publicMeetingTypes = meetingTypeStates.publicMeetingTypes;
    const privateMeetingTypes = meetingTypeStates.privateMeetingTypes;

    logUsage('deleteMeetingType', {
        entryPoint: getEntryPoint(),
        isNewUser: getFirstTimeUser(),
        isDefault: isDefaultMT,
        bookingMailboxPuid: getMailboxPuid(),
        meetingTypesCount: publicMeetingTypes.length + privateMeetingTypes.length,
        isPublished: store.pbpPublishingSetting.publishingSettings.IsPublished,
        isUnified: true,
    });

    if (isFeatureEnabled('bookwithme-c1B2Migration')) {
        const perfDatapoint = new PerformanceDatapoint('deleteBookingMeetingType');
        addB2CustomData(perfDatapoint);
        perfDatapoint.addCustomData({
            meetingTypeId,
            entryPoint: getEntryPoint(),
            isNewUser: getFirstTimeUser(),
            mailboxGuid: getUserMailboxGuid(),
            bookingMailboxPuid: getMailboxPuid(),
        });
        if (
            (await confirm(
                loc(bookWithMeDeleteButtonLabel),
                loc(bookWithMeDeleteConfirmationText),
                false,
                {}
            )) == DialogResponse.ok
        ) {
            let deletedMeetingType = false;
            if (meetingTypeId) {
                try {
                    setIsPerformingServiceUpdate(true);
                    if (store.meetingTypes) {
                        const meetingType = store.meetingTypes.get(meetingTypeId);
                        if (meetingType?.ItemId) {
                            const bookingsSMTPAddress =
                                getUserConfiguration().SessionSettings?.UserPrincipalName || '';
                            const result = await deleteBookingService(
                                bookingsSMTPAddress,
                                meetingTypeId,
                                'BookWithMe'
                            );
                            //TODO: Check if the Service is present in SDS before calling deleteMeetingTypeExtension
                            //https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/188870
                            deleteMeetingTypeExtension(meetingTypeId);
                            if (!result.hasError) {
                                deleteMeetingTypeAction(meetingType?.ItemId);
                                deletedMeetingType = true;
                            }
                        }
                    }
                } catch (e) {
                    trace.warn(e.message || 'deleteMeetingType failed');
                    perfDatapoint.endWithError(DatapointStatus.ServerError);
                } finally {
                    setIsPerformingServiceUpdate(false);
                }
            }
            if (deletedMeetingType) {
                lazyShowNotification.importAndExecute(
                    'bookWithMeDeleteEventSuccess',
                    'EventifyNotificationBarHost',
                    window,
                    loc(bookWithMeDeleteEventSuccess),
                    { primaryActionHotKeyCommand: '' }
                );
                await getMeetingTypesFromServer();
                const currentRoute = getBookingsRouterStore().currentRoute;
                if (currentRoute === 'newmeetingtypecompose') {
                    setTimeout(() => {
                        setNavigationStart(performance.now());
                        changeCurrentRoute('homepage');
                    }, 10);
                } else {
                    lazyOpenBookingDashboard.importAndExecute(null, 'sidebar');
                }
            } else {
                lazyShowNotification.importAndExecute(
                    'bookWithMeDeleteEventFailure',
                    'EventifyNotificationBarHost',
                    window,
                    loc(bookWithMeDeleteEventFailure),
                    { primaryActionHotKeyCommand: '' }
                );
            }
        }
    } else {
        if (
            (await confirm(
                loc(bookWithMeDeleteButtonLabel),
                loc(bookWithMeDeleteConfirmationText)
            )) == DialogResponse.ok
        ) {
            let deletedMeetingType = false;
            if (meetingTypeId) {
                if (isDefaultMT) {
                    deletedMeetingType = await deleteDefaultMeetingType();
                    await deleteMeetingTypeExtension(meetingTypeId);
                } else {
                    try {
                        setIsPerformingServiceUpdate(true);
                        if (store.meetingTypes) {
                            const meetingType = store.meetingTypes.get(meetingTypeId);
                            if (meetingType?.ItemId) {
                                const result = await deleteBookingMeetingType(
                                    meetingType.ItemId,
                                    meetingTypeId
                                );
                                if (result?.DeletedId) {
                                    deleteMeetingTypeAction(result.DeletedId);
                                    deletedMeetingType = true;
                                }
                            }
                        }
                    } catch (e) {
                        trace.warn(e.message || 'deleteMeetingType failed');
                    } finally {
                        setIsPerformingServiceUpdate(false);
                    }
                }
            }
            if (deletedMeetingType) {
                lazyShowNotification.importAndExecute(
                    'bookWithMeDeleteEventSuccess',
                    'EventifyNotificationBarHost',
                    window,
                    loc(bookWithMeDeleteEventSuccess),
                    { primaryActionHotKeyCommand: '' }
                );
                await getMeetingTypesFromServer();
                const currentRoute = getBookingsRouterStore().currentRoute;
                if (currentRoute === 'newmeetingtypecompose') {
                    setTimeout(() => {
                        setNavigationStart(performance.now());
                        changeCurrentRoute('homepage');
                    }, 10);
                } else {
                    lazyOpenBookingDashboard.importAndExecute(null, 'sidebar');
                }
            } else {
                lazyShowNotification.importAndExecute(
                    'bookWithMeDeleteEventFailure',
                    'EventifyNotificationBarHost',
                    window,
                    loc(bookWithMeDeleteEventFailure),
                    { primaryActionHotKeyCommand: '' }
                );
            }
        }
    }
}
